// Installer l'extension : depuis le Chrome Web Store, où elle est publiée
// depuis le 2026-10-09.
//
// La fiche du store est LE chemin : un clic, aucune manipulation. Le zip et le
// mode développeur restent, repliés, pour qui ne peut pas passer par le store
// (Chromium sans store, poste d'entreprise qui le bloque) : il ne faut plus
// qu'un invité les voie en premier, c'était une marche que beaucoup ne
// montaient pas.

import { Download, ExternalLink } from 'lucide-react'
import { ficheWebStore } from '@ai-job-fit/engine/webStore'
import { get } from '../lib/api'
import { ActionButton, useData, useToast } from './common'
import { connecterLExtension } from '../lib/extensionInstallee'
import { useT } from '../lib/i18n'
import { useLang } from '../lib/lang'
import { mesurer } from '../lib/mesure'

interface EtatExtension {
  version: string
  disponible: boolean
}

/**
 * Un ordinateur, pas un téléphone : une extension Chrome ne s'installe pas au
 * téléphone, et la proposer là mène à une fiche du store où rien ne marche.
 * Le pointeur fin qui survole est le meilleur signe disponible sans deviner
 * le modèle d'après l'agent utilisateur.
 */
export function surOrdinateur(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover) and (pointer: fine)').matches
}

/** Le bouton « Ajouter à Chrome », partagé par l'aide et l'accueil. */
export function BoutonWebStore({ petit }: { petit?: boolean }) {
  const t = useT()
  const lang = useLang()
  return (
    <a
      className={petit ? 'btn-primary btn-sm' : 'btn-primary'}
      href={ficheWebStore(lang)}
      target="_blank"
      rel="noopener noreferrer"
      // Le dernier pas que Radar voit avant l'installation : le store ne dit
      // rien de qui installe, l'extension seulement qu'elle est installée.
      onClick={() => mesurer('radar_clic', { cible: 'chrome_store', place: petit ? 'accueil' : 'aide' })}
    >
      {t('aide.step.install.store')} <ExternalLink size={14} aria-hidden="true" />
    </a>
  )
}

export function InstallerExtension() {
  const t = useT()
  return (
    <div className="installer">
      <p>
        <BoutonWebStore />
      </p>
      <ol className="etapes-liste">
        <li>{t('aide.step.install.storeS1')}</li>
        <li>{t('aide.step.install.s4')}</li>
      </ol>
      <p className="field-help">{t('aide.step.install.sites')}</p>
      <details className="installer-zip">
        <summary>{t('aide.step.install.zipTitre')}</summary>
        <InstallerParZip />
      </details>
    </div>
  )
}

/** Le repli : le zip de la version servie, chargé en mode développeur. */
export function InstallerParZip() {
  const t = useT()
  const extension = useData(() => get<EtatExtension>('/api/extension'), [])
  const version = extension.data?.version ?? ''
  return (
    <>
      <p className="field-help">{t('aide.step.install.zipQuand')}</p>
      {extension.data?.disponible ? (
        <p>
          <a className="btn-secondary" href="/api/extension/download">
            <Download size={15} /> {t('aide.step.install.download', { version })}
          </a>
        </p>
      ) : (
        !extension.loading && <p className="field-error">{t('aide.step.install.unavailable', { version })}</p>
      )}
      <ol className="etapes-liste">
        <li>{t('aide.step.install.s1')}</li>
        <li>{t('aide.step.install.s2')}</li>
        <li>{t('aide.step.install.s3', { version })}</li>
      </ol>
    </>
  )
}

/**
 * L'extension est installée mais pas connectée (issue #234) : un clic, et
 * Radar lui passe un code qu'elle échange elle-même. Plus d'onglet à ouvrir
 * depuis ses Options, plus de code à recopier.
 */
export function BoutonConnecterExtension({ petit, onConnectee }: { petit?: boolean; onConnectee: () => void }) {
  const t = useT()
  const toast = useToast()
  return (
    <ActionButton
      variant="primary"
      size={petit ? 'sm' : undefined}
      onClick={async () => {
        const r = await connecterLExtension()
        if (r.ok) {
          toast(t('ext.connectee'))
          onConnectee()
        } else {
          toast(t(r.raison === 'sans-cv' ? 'ext.echecSansCv' : 'ext.echec'), 'err')
        }
      }}
    >
      {t('ext.connecter')}
    </ActionButton>
  )
}
